import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown, ArrowLeft } from 'lucide-react'
import DiscordIcon from '@/components/site/DiscordIcon'
import useDiscordInvite from '@/components/site/useDiscordInvite'
import { HOME_SITE_URL } from '@/lib/site'
import { useLanguage } from '@/contexts/LanguageContext'
import { type Language } from '@/i18n/translations'

/**
 * Page chrome for join.turksletloose.com — the same photograph, scrim and
 * graphite surfaces as the dashboard, minus the sidebar. Applicants are not
 * signed into the dashboard, so nothing here touches AuthContext.
 *
 * `showNav` adds the section links. They are anchor jumps, so only the landing
 * page (which owns those sections) asks for them.
 */
export default function JoinShell({
  children,
  showNav = false,
}: {
  children: ReactNode
  showNav?: boolean
}) {
  const { lang, setLang, T } = useLanguage()
  const copyright = T.join.copyright.replace('{year}', String(new Date().getFullYear()))

  // index.html is shared with the dashboard, so its <title> is the dashboard's.
  // The join site renames the tab for itself, and follows the language switch.
  useEffect(() => {
    document.title = T.join.pageTitle
  }, [T])

  const inviteUrl = useDiscordInvite()

  // On the landing page the bar is not pinned over the hero: it scrolls away
  // with the page, then drops back down once you are past the fold. Elsewhere
  // (the apply page) it is a plain sticky bar, so it is always in place.
  const [pinned, setPinned] = useState(false)
  useEffect(() => {
    if (!showNav) return
    const onScroll = () => setPinned(window.scrollY > 220)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [showNav])

  // Labels are the section headings themselves, so the nav and the page can
  // never drift apart.
  const sections = [
    { href: '#what',   label: T.join.pillars.title },
    { href: '#expect', label: T.join.requirements.title },
    { href: '#how',    label: T.join.steps.title },
    { href: '#faq',    label: T.join.faq.title },
  ]

  const bar = (
    <div className="mx-auto flex h-[68px] w-full max-w-5xl items-center gap-3 px-5">
      {/* The wordmark leaves for the community site: this hostname only holds
          the application flow, so there is no "/" here worth returning to. */}
      <a href={HOME_SITE_URL} className="flex min-w-0 items-center gap-3">
        <img src="/tll_logo.png" alt="" className="h-10 w-10 rounded-md object-contain" />
        <div className="min-w-0">
          <p className="font-display text-sm font-bold uppercase leading-none tracking-tight">
            {T.join.brand}
          </p>
          <p className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-muted-foreground">
            {T.join.tagline}
          </p>
        </div>
      </a>

      {/* The way back to the community site is not in here: among this page's
          own section anchors it reads as one more section of it. It sits on its
          own row under the bar instead, with a back arrow. */}
      {showNav && (
        <nav className="ml-6 hidden items-center gap-5 lg:flex">
          {sections.map(sec => (
            <a
              key={sec.href}
              href={sec.href}
              className="font-display text-[13px] font-semibold tracking-tight text-foreground/70 transition-colors hover:text-foreground"
            >
              {sec.label}
            </a>
          ))}
        </nav>
      )}

      <div className="ml-auto flex items-center gap-2">
        {inviteUrl && (
          <a
            href={inviteUrl}
            target="_blank"
            rel="noreferrer"
            title={T.join.discordCta}
            className="flex items-center gap-2 rounded-sm border border-white/12 bg-white/5 px-3 py-1.5 text-[11px] font-bold tracking-wider text-foreground/80 transition-colors hover:bg-white/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <DiscordIcon size={14} />
            <span className="hidden sm:inline">{T.join.discordCta}</span>
          </a>
        )}

        <div className="relative">
          <select
            value={lang}
            onChange={e => setLang(e.target.value as Language)}
            aria-label={T.language.select}
            className="cursor-pointer appearance-none rounded-sm border border-white/12 bg-white/5 py-1.5 pl-3 pr-7 text-[11px] font-bold tracking-wider text-foreground/80 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="en">EN</option>
            <option value="tr">TR</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>
    </div>
  )

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0b0b0d]">
      {/* Background photograph — fixed so the page scrolls over it */}
      <div
        className="pointer-events-none fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/background.webp')" }}
      >
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: '160px 160px',
          }}
        />
      </div>
      <div className="pointer-events-none fixed inset-0 bg-black/70" />

      <div className="relative flex min-h-screen flex-col">
        {/* Top bar.
            The landing page gets two of these: one sitting in normal flow that
            scrolls away with the hero, and one fixed copy that drops back down
            once you are past the fold. Rendering the same bar twice keeps the
            logo and the language switcher reachable at every scroll position,
            which a single fixed-and-hidden bar would not. */}
        {showNav && (
          <header className="relative z-20 border-b border-white/10 bg-black/25 backdrop-blur-sm">
            {bar}
          </header>
        )}

        <header
          className={
            showNav
              ? `fixed inset-x-0 top-0 z-30 border-b border-white/10 bg-black/70 backdrop-blur-md
                 transition-transform duration-300 ease-out
                 ${pinned ? 'translate-y-0' : '-translate-y-full'}`
              : 'sticky top-0 z-20 border-b border-white/10 bg-black/40 backdrop-blur-md'
          }
          aria-hidden={showNav && !pinned}
        >
          {bar}
        </header>

        <div className="relative z-10 mx-auto w-full max-w-5xl px-5 pt-4">
          <a
            href={HOME_SITE_URL}
            className="inline-flex items-center gap-1.5 font-display text-[12px] font-semibold tracking-tight text-foreground/60 transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {T.join.homeLink}
          </a>
        </div>

        <main className="flex-1">{children}</main>

        <footer className="border-t border-white/10 bg-black/40 backdrop-blur-md">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-1 px-5 py-6 text-center">
            <p className="text-[11px] text-muted-foreground/70">{copyright}</p>
            <p className="text-[11px] text-muted-foreground/50">{T.join.footerNote}</p>
          </div>
        </footer>
      </div>
    </div>
  )
}
